/* eslint-disable lingui/no-unlocalized-strings */
// Apply Edge — Security page. Port of design/source/public/security.html.
// Chrome (NavBar/Footer) is owned by HomeLayout — do not render here.
//
// IMPORTANT: do NOT claim "SOC 2 Type II certified". The honest status is
// "In progress · audit complete Q3 2026". Reflect verbatim from design.
import { t, Trans } from "@lingui/macro";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

type StatProps = { ariaLabel?: string; k: string; v: string; sub: string };
const Stat = ({ ariaLabel, k, v, sub }: StatProps) => (
  <div
    aria-label={ariaLabel}
    className="rounded-xl border border-border bg-surface p-5 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md"
  >
    <div className="text-xs font-medium text-muted-foreground">{k}</div>
    <div className="mt-3 font-serif text-[28px] font-semibold leading-none tracking-tight text-foreground">
      {v}
    </div>
    <div className="mt-2 text-[12px] leading-[1.55] text-muted-foreground">{sub}</div>
  </div>
);

const PILLARS = [
  {
    title: "Encryption",
    items: [
      "TLS 1.3 in transit, AES-256-GCM at rest, on every layer of the stack.",
      "Per-tenant encryption keys for resume content, rotated quarterly.",
      "Database-level row encryption for personally-identifying fields.",
    ],
  },
  {
    title: "Access control",
    items: [
      "Production access is MFA-only, role-scoped, and short-lived (90-day max session).",
      "All admin actions logged immutably. Audit trail searchable by support, reviewed weekly.",
      "Engineers cannot read user resume content in production without break-glass approval and user consent.",
    ],
  },
  {
    title: "AI processing",
    items: [
      "Inference via Anthropic and OpenAI under zero-retention agreements — your prompts and outputs are not stored on their side beyond the API call.",
      "Your content is never used to train any model — ours, theirs, or anyone else's.",
      "Optional EU-only inference path on Ultimate (Frankfurt region, no cross-Atlantic transit).",
    ],
  },
  {
    title: "Infrastructure",
    items: [
      "AWS, multi-AZ in eu-central-1 (primary) and us-east-1 (replica).",
      "Daily encrypted backups, 30-day retention, geographically separated.",
      "Tested DR plan — quarterly recovery drills documented in our SOC 2 program.",
    ],
  },
  {
    title: "Application security",
    items: [
      "OWASP Top 10 covered by code review, automated SAST (Semgrep), dependency scanning (Snyk), and quarterly external pentests.",
      "Responsible disclosure is open to security researchers, with clear acknowledgement and triage windows.",
      "Strict CSP, HSTS, SRI, and signed cookies. No third-party trackers on logged-in surfaces.",
    ],
  },
  {
    title: "People & process",
    items: [
      "All staff sign a confidentiality agreement and complete annual security training.",
      "Background checks for everyone with production access.",
      "Incident response runbook with 15-minute paging, public postmortem within 5 business days for any user-affecting incident.",
    ],
  },
] as const;

type Dot = "success" | "warning" | "muted";
const COMPLIANCE: readonly { name: string; status: string; dot: Dot }[] = [
  { name: "SOC 2 Type II", status: "In progress · audit complete Q3 2026", dot: "warning" },
  { name: "GDPR", status: "Compliant — DPA available on request", dot: "success" },
  { name: "UK GDPR", status: "Compliant", dot: "success" },
  { name: "CCPA / CPRA", status: "Compliant", dot: "success" },
  {
    name: "ISO 27001",
    status: "Pre-assessment underway, certification target 2027",
    dot: "muted",
  },
  {
    name: "HIPAA",
    status: "Out of scope — Apply Edge does not process health data",
    dot: "muted",
  },
];

const dotClass: Record<Dot, string> = {
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  muted: "bg-muted-foreground/40",
};

const PGP_BLOCK =
  "PGP key coming soon — for now, send sensitive disclosures to security@apply-edge.com and we'll respond with a key on first contact.";

export const SecurityPage = () => (
  <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
    <SEO
      title={t`Security — Apply Edge`}
      description={t`How Apply Edge protects your data — encryption, access control, AI processing, compliance, and responsible disclosure.`}
      canonical="/security"
    />

    {/* Hero */}
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Security</span>
            <h1 className="mt-3 font-serif text-[44px] font-semibold leading-[1.04] tracking-tight text-foreground sm:text-[60px]">
              Built like the data <span className="italic">matters</span>.
            </h1>
            <p className="mt-5 max-w-xl text-[15.5px] leading-[1.7] text-muted-foreground">
              Your resume is a long, honest record of your career. We treat it that way. This page
              details the controls we use; the{" "}
              {/* [QA-M] /privacy-policy is a 404 — canonical route is /privacy */}
              <Link className="text-primary hover:underline" to="/privacy">
                privacy policy
              </Link>{" "}
              covers the rights you have.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="mailto:security@apply-edge.com"
                className="rounded-lg bg-primary px-4 py-2.5 text-xs font-medium text-primary-foreground"
              >
                Contact security
              </a>
              <a
                href="#disclosure"
                className="rounded-lg border border-border bg-background px-4 py-2.5 text-xs font-medium text-muted-foreground"
              >
                Vulnerability disclosure
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat k="Encryption" v="AES-256" sub="At rest, with TLS 1.3 in transit" />
            <Stat k="SOC 2" v="In progress" sub="Audit complete Q3 2026" />
            <Stat
              ariaLabel="Open responsible disclosure program"
              k="Disclosure program"
              v="Open"
              sub="Responsible disclosure"
            />
            <Stat k="Incident response" v="Sub-hour" sub="MTTR target for critical incidents" />
          </div>
        </div>
      </div>
    </section>

    {/* Pillars */}
    <section className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <h2 className="font-serif text-[28px] font-semibold tracking-tight text-foreground">
          Six pillars
        </h2>
        <p className="mt-2 max-w-2xl text-[14px] text-muted-foreground">
          Security isn&apos;t a feature; it&apos;s the order in which we make decisions. Here&apos;s
          what that looks like in practice.
        </p>
        <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((p, i) => (
            <div key={p.title}>
              <div className="text-xs font-medium text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-2 font-serif text-[20px] font-semibold tracking-tight text-foreground">
                {p.title}
              </h3>
              <ul className="mt-4 space-y-3 text-[13.5px] leading-[1.65] text-foreground/85">
                {p.items.map((it) => (
                  <li key={it} className="flex gap-2.5">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-foreground/40" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Compliance */}
    <section className="border-b border-border bg-surface">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Compliance</span>
            <h2 className="mt-3 font-serif text-[28px] font-semibold leading-[1.1] tracking-tight text-foreground">
              Where we <span className="italic">stand</span>, plainly
            </h2>
            <p className="mt-3 text-[14px] leading-[1.65] text-muted-foreground">
              No marketing-speak. If we&apos;re done, we say done. If we&apos;re working on it, we
              say what month.
            </p>
          </div>
          <div className="divide-y divide-border rounded-2xl border border-border bg-background">
            {COMPLIANCE.map((c) => (
              <div key={c.name} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="flex items-center gap-3">
                  <span className={"h-2 w-2 rounded-full " + dotClass[c.dot]} />
                  <div className="text-[14px] font-medium text-foreground">{c.name}</div>
                </div>
                <div className="text-right text-[12.5px] text-muted-foreground">{c.status}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Disclosure + PGP */}
    <section id="disclosure" className="border-b border-border">
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Disclosure</span>
            <h2 className="mt-3 font-serif text-[28px] font-semibold leading-[1.1] tracking-tight text-foreground">
              Found a vulnerability? <span className="italic">Tell us.</span>
            </h2>
            <p className="mt-4 text-[14.5px] leading-[1.7] text-foreground/85">
              Found a vulnerability? Tell us. We run a responsible disclosure program — no money,
              but we&apos;ll thank you publicly, fast. We acknowledge within 24h and triage valid
              reports within 5 days. Researchers who help us improve are listed at{" "}
              <Link className="text-primary hover:underline" to="/security/researchers">
                apply-edge.com/security/researchers
              </Link>
              .
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-[12.5px] text-muted-foreground">
              <div className="rounded-xl border border-border bg-surface p-4 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                <div className="text-xs font-medium text-muted-foreground">Acknowledge</div>
                <div className="mt-2 font-serif text-[20px] font-semibold leading-none tracking-tight text-foreground">
                  &lt; 24h
                </div>
                <p className="mt-2">Median 4h on weekdays.</p>
              </div>
              <div className="rounded-xl border border-border bg-surface p-4 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                <div className="text-xs font-medium text-muted-foreground">Triage</div>
                <div className="mt-2 font-serif text-[20px] font-semibold leading-none tracking-tight text-foreground">
                  &lt; 5 days
                </div>
                <p className="mt-2">For valid reports.</p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-surface p-6 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
            <div className="text-xs font-medium text-muted-foreground">PGP</div>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all rounded-lg border border-border bg-background p-3 font-code text-[11px] leading-[1.55] text-foreground/80 md:whitespace-pre md:break-normal">
              {PGP_BLOCK}
            </pre>
            <p className="mt-4 text-[12.5px] leading-[1.6] text-muted-foreground">
              For sensitive disclosures, use email for now. We will respond with a key on first
              contact.
            </p>
            <a
              href="mailto:security@apply-edge.com?subject=PGP%20key%20request"
              className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-border bg-background px-3.5 py-2 text-xs font-medium text-muted-foreground hover:border-foreground/30 md:min-h-0"
            >
              <Trans>Request PGP key</Trans>
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Enterprise CTA */}
    <section>
      <div className="mx-auto max-w-[1280px] px-6 py-20">
        <div className="rounded-2xl border border-border bg-foreground p-10 text-background">
          <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <span className="text-xs font-medium text-background">Enterprise</span>
              <h2 className="mt-3 font-serif text-[32px] font-semibold leading-[1.05] tracking-tight">
                Need a DPA, SOC 2 report, or custom data residency?
              </h2>
              <p className="mt-3 max-w-xl text-[14px] leading-[1.65] opacity-75">
                For employer accounts and bulk-seat plans. We also ship custom retention windows,
                SAML SSO, and audit log export.
              </p>
            </div>
            <div className="flex flex-col gap-3 lg:items-end">
              <a
                href="mailto:enterprise@apply-edge.com"
                className="inline-flex min-h-11 items-center rounded-lg bg-background px-4 py-2.5 text-xs font-medium text-muted-foreground md:min-h-0"
              >
                enterprise@apply-edge.com
              </a>
              <a
                href="mailto:enterprise@apply-edge.com?subject=SOC%202%20letter%20request"
                className="inline-flex min-h-11 items-center text-xs font-medium text-background hover:underline md:min-h-0"
              >
                {t`Request SOC 2 letter →`}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);
